import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  InputLabel,
  Divider,
  Select,
  MenuItem,
  Stack,
} from "@mui/material";
import {
  Article,
} from "@mui/icons-material"; // Import icons
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import RichText from "../../components/richText/RichText";
import { useForm, Controller } from "react-hook-form";
import InputBox from "../../themes/overrides/InputBox";
import { Tabs, Tab } from '@mui/material';
import AddButton from "../../themes/overrides/AddButton";
import StyledTextArea from "../../themes/overrides/TextArea";
import ModalBox from "../../themes/overrides/ModalBox";
import OutlinedInput from "../../themes/overrides/OutlinedInput";
import NumberIcon from "../../components/icons/NumberIcon";
import ObjectIcon from "../../components/icons/ObjectIcon";
import RefArrayIcon from "../../components/icons/RefArrayIcon";
import RefIcon from "../../components/icons/RefIcon";
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import TextIcon from "../../components/icons/TextIcon";
import RichTextIcon from "../../components/icons/RichTextIcon";
import MediaIcon from "../../components/icons/MediaIcon";
import BooleanIcon from "../../components/icons/BooleanIcon";
import ColorIcon from "../../components/icons/ColorIcon";
import DateIcon from "../../components/icons/DateIcon";
import DateTimeEntry from "../../components/DateTimeEntry";
import FormatSizeIcon from '@mui/icons-material/FormatSize';
import CodeIcon from "../../components/icons/CodeIcon";


const FieldModal = ({ isOpen, onClose, onAddField, handleFieldChange, fieldData, editMode }) => {
  const { register, handleSubmit, reset, setValue, control, setError, clearErrors, formState: { errors }, watch } = useForm();
  const allFields = useSelector((state) => state.form.fields) || [];
  const selectedType = watch('type');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeTab, setActiveTab] = useState(0);
  const [selectedDisplayOption, setSelectedDisplayOption] = useState("default");
  const [selectedArrayType, setSelectedArrayType] = useState('string');
  const [selectedStringConfig, setSelectedStringConfig] = useState('none');
  const [AIPrompt, setAIPrompt] = useState('');
  const [specificValues, setSpecificValues] = useState(['']);
  const installedApps = useSelector((state) => state.workspace.workspaceInfo.data.installedApps);
  const [selectedApp, setSelectedApp] = useState(null);
  const [arrayFieldSettings, setArrayFieldSettings] = useState(['localizations', 'required']);
  const [selectedReferenceSlug, setSelectedReferenceSlug] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('javascript');

  const [fieldEdit, setFieldEdit] = useState(false);

  useEffect(() => {
    if (fieldData) {
      setValue("name", fieldData.name || "");
      setValue("type", fieldData.type || "");
      setValue("id", fieldData.id || "");
      setValue("variant", fieldData.variant || "");
      setValue("default_value", fieldData.default_value || "");
      setValue("display", fieldData.display || "");
      setValue("ai_prompt", fieldData.ai_prompt || "");
      setValue("array_type", fieldData.array_type || "");
      setValue("localizations", fieldData.localizations || false);
      setValue("required", fieldData.required || false);
      setValue("version_control", fieldData.version_control || false);
      setValue("enable_ai", fieldData.enable_ai || false);

      if (fieldData.variant === "slugField") {
        setValue("referenceSlugEntry", fieldData.referenceSlugEntry || "");
        setSelectedReferenceSlug(fieldData.referenceSlugEntry || "");
      }

      // Handle string specific values and default values
      if (fieldData.type === 'string') {
        if (fieldData.specific_values && fieldData.specific_values.length > 0) {
          setSelectedStringConfig('specific_value');
          setSpecificValues(fieldData.specific_values);
          setValue("specific_values", fieldData.specific_values);
        } else if (fieldData.default_value) {
          setSelectedStringConfig('default_value');
        } else {
          setSelectedStringConfig('none');
        }
      }

      // Handle array type
      if (fieldData.type === 'array') {
        setSelectedArrayType(fieldData.array_type || 'string');
      }

      if (fieldData.type === 'code') {
        setSelectedLanguage(fieldData.code_language || 'javascript');
        setValue("code_language", fieldData.code_language || 'javascript');
      }

      // Handle object display option
      if (fieldData.type === 'object') {
        if (fieldData.display && fieldData.display.appName) {
          setSelectedDisplayOption('customApp');
          // Find the matching app in installedApps
          const matchingApp = installedApps.find(app =>
            app.appName === fieldData.display.appName &&
            app.url === fieldData.display.url
          );
          if (matchingApp) {
            setSelectedApp(matchingApp._id);
          }
        } else {
          setSelectedDisplayOption('default');
        }
      }

    } else {
      reset();
      setSelectedStringConfig('none');
      setSpecificValues(['']);
      setSelectedArrayType('string');
      setSelectedLanguage('javascript');
      setSelectedDisplayOption('default');
      setSelectedReferenceSlug('');
      setSelectedApp(null);
    }
  }, [fieldData, setValue, reset, installedApps]);

  useEffect(() => {
    if ((selectedType && selectedType !== 'string')) {
      setSelectedStringConfig('none');
      setSpecificValues(['']);
    }
  }, [selectedType]);

  useEffect(() => {
    if (selectedType === 'array') {
      if (selectedArrayType === 'number') {
        setArrayFieldSettings(['required']);
      } else {
        setArrayFieldSettings(['localizations', 'required']);
      }
    }
  }, [selectedArrayType, selectedType]);

  useEffect(() => {
    if (fieldData !== null) {
      setFieldEdit(true);
    }
  }, [fieldData, fieldEdit, setFieldEdit])


  useEffect(() => {
    setAIPrompt(fieldData?.ai_prompt || '');
  }, [fieldData]);


  const fieldSettings = {
    string: ['localizations', 'required', 'enable_ai'],
    number: ['required'],
    richText: ['localizations', 'required', 'enable_ai'],
    dateTime: ['required'],
    boolean: ['required'],
    color: ['required'],
    media: ['required'],
    reference: ['required'],
    array: arrayFieldSettings,
    object: [...(selectedDisplayOption === 'default' ? ['localizations'] : []), 'required'],
    code: ['required', 'enable_ai']
  };

  const stringConfigurations = [
    {
      value: "shortText",
      label: "Short Text",
      icon: <TextIcon />,
      description: 'For titles, names, short answers'
    },
    {
      value: "longText",
      label: "Long Text",
      icon: <Article />,
      description: 'For paragraphs, descriptions, long form text'
    },
    {
      value: "slugField",
      label: "Slug Field",
      icon: <FormatSizeIcon />,
      description: "Generate a URL-friendly slug automatically from another field (e.g., blog-title)"
    }
  ];

  const fieldTypes = [
    { value: "string", label: "String", icon: <TextIcon />, description: 'Titles, names, paragraphs, list of names' },
    { value: "number", label: "Number", icon: <NumberIcon />, description: 'ID, Order number, rating, Quantity' },
    { value: "richText", label: "Rich Text", icon: <RichTextIcon />, description: 'Text formatting with references and media' },
    { value: "dateTime", label: "Date & Time", icon: <DateIcon />, description: 'Event dates, Times and more' },
    { value: "boolean", label: "Boolean", icon: <BooleanIcon />, description: 'True or False, 1 or 0, Yes or No' },
    { value: "media", label: "Media", icon: <MediaIcon />, description: 'Images, videos, PDFs and other files' },
    { value: "reference", label: "Reference", icon: <RefIcon />, description: 'A blog post can be reference its author,' },
    { value: "array", label: "Array", icon: <RefArrayIcon />, description: 'Store multiple values in a single variable' },
    { value: "object", label: "Object", icon: <ObjectIcon />, description: 'Data in JSON format' },
    { value: "code", label: "Code", icon: <CodeIcon />, description: 'Code snippets (HTML, JS, CSS, etc.)' },
    { value: "color", label: "Color", icon: <ColorIcon />, description: 'Solid color or gradient (CSS value)' },
  ];

  const defaultValueConfig = {
    number: {
      type: 'text',
      label: 'Default value (number)'
    },
    richText: {
      type: 'richText',
      label: 'Default value (rich text)'
    },
    dateTime: {
      type: 'datetime',
      label: 'Default value (date & time)'
    }
  };

  const clearFields = () => {
    setActiveTab(0);
    setSelectedApp(null);
    setSelectedDisplayOption('default');
    setSelectedStringConfig('none');
    setSpecificValues(['']);
    setSelectedReferenceSlug('')
    setSelectedLanguage('javascript');
    setAIPrompt('');
    setFieldEdit(false);
  }

  // Reset display option when field type changes (unless it's 'object')
  useEffect(() => {
    if (selectedType !== 'object') {
      setSelectedDisplayOption('default');
    }
  }, [selectedType]);

  useEffect(() => {
    if (selectedDisplayOption === 'customApp') {
      setValue('localizations', false); // Reset localizations when switching to customApp
    }
  }, [selectedDisplayOption, setValue]);


  const onSubmit = (data) => {
    if (checkDuplicateId(data.id)) {
      setError('id', {
        type: 'manual',
        message: 'This ID already exists in the form fields'
      });
      return;
    }

    // Name validation
    const fieldName = watch('name');
    if (!validateField('name', fieldName)) {
      return;
    }

    // Id Validation
    const currentId = watch('id');
    if (!validateField('id', currentId, 3, checkDuplicateId)) {
      return;
    }

    const versionControlTypes = ['string', 'number', 'richText', 'media', 'dateTime', 'array', 'code'];

    if (versionControlTypes.includes(data.type)) {
      data.version_control = true;
    } else {
      data.version_control = false;
    }
    if (selectedType === 'string' && watch('variant') === 'shortText') {
      if (selectedStringConfig === 'default_value' && !data.default_value?.trim()) {
        setError('default_value', {
          type: 'manual',
          message: 'Default value is required'
        });
        return;
      }

      if (selectedStringConfig === 'specific_value') {
        const validValues = specificValues.filter(value => value.trim() !== '');
        if (validValues.length === 0) {
          setError('specific_values', {
            type: 'manual',
            message: 'At least one specific value is required'
          });
          return;
        }
        data.specific_values = validValues;
        clearErrors('specific_values');
      }
    }

    // Used for setting find field
    if (selectedType === 'string' || selectedType === 'number') {
      data.findable = watch('findable') || false;
    }

    onAddField(data);
    clearFields();
    reset();
    onClose();
  };

  const checkDuplicateId = (id) => {
    return editMode
      ? allFields.some(field => field.id === id && field.id !== fieldData?.id)
      : allFields.some(field => field.id === id);
  };

  useEffect(() => {
    if (selectedType && !editMode) {
      setValue('variant', '');
      setValue('default_value', '');
    }
  }, [selectedType, setValue]);

  useEffect(() => {
    const variant = watch('variant');
    const type = watch('type');
    if (type === 'string' && variant && variant !== 'shortText') {
      setValue('default_value', '');
    }
  }, [watch('variant'), watch('type'), setValue]);

  useEffect(() => {
    // If selectedStringConfig is 'specific_value', remove ai_prompt field
    if (selectedStringConfig === 'specific_value') {
      setValue("ai_prompt", undefined);
      setValue('enable_ai', false);
    }
  }, [selectedStringConfig, setValue]);

  const validateField = (fieldName, value, minLength = 3, checkDuplicate = null) => {
    if (!value) {
      setError(fieldName, {
        type: 'manual',
        message: `Field ${fieldName} is required`,
      });
      return false;
    }

    if (value.trim() === '') {
      setError(fieldName, {
        type: 'manual',
        message: `Field ${fieldName} cannot be just spaces.`,
      });
      return false;
    }

    if (value.length < minLength) {
      setError(fieldName, {
        type: 'manual',
        message: `Field ${fieldName} must be at least ${minLength} characters.`,
      });
      return false;
    }

    if (checkDuplicate && checkDuplicate(value)) {
      setError(fieldName, {
        type: 'manual',
        message: `Field ${fieldName} already exists.`,
      });
      return false;
    }

    return true;
  };


  const handleTabChange = (event, newValue) => {
    // if (!watch('type')) {
    //   setError('type', {
    //     type: 'manual',
    //     message: 'Please select a field type'
    //   });
    //   return;
    // }

    // // Get current settings values before tab change
    // const currentType = watch('type');
    // if (currentType && fieldSettings[currentType]) {
    //   fieldSettings[currentType].forEach(setting => {
    //     const value = watch(setting);
    //     setValue(setting, value);
    //   });
    // }

    setActiveTab(newValue);
  };

  // const handleNext = () => {
  //   if (!watch('type')) {
  //     setError('type', {
  //       type: 'manual',
  //       message: 'Please select a field type'
  //     });
  //     return;
  //   }

  //   // Name validation
  //   const fieldName = watch('name');
  //   if (!validateField('name', fieldName)) {
  //     return;
  //   }

  //   // Id Validation
  //   const currentId = watch('id');
  //   if (!validateField('id', currentId, 3, checkDuplicateId)) {
  //     return;
  //   }

  //   setActiveTab(1);
  // };

  const validateNumber = (value) => {
    if (value === '') return true;
    return !isNaN(value) && Number.isFinite(Number(value));
  };

  // const transformFieldId = (name) => {
  //   const splitWords = name.split(/[\s-]+/);
  //   const camelTypeId = splitWords
  //     .map((splitWord, index) =>
  //       index === 0 ?
  //         splitWord.toLowerCase()
  //         : splitWord.charAt(0) + splitWord.slice(1).toLowerCase()
  //     ).join("");
  //   setValue("id", camelTypeId);
  // };

  const transformFieldId = (name) => {
    const words = name.split(/[\s-]+/);
    const camelCaseSlug = words
      .map((word, index) =>
        index === 0
          ? word.toLowerCase()
          : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      )
      .join("");
    setValue("id", camelCaseSlug);

  };

  const handleAIPromptChange = (value) => {
    setAIPrompt(value)
    clearErrors(['enable_ai']);
    setValue("ai_prompt", value)
  }

  useEffect(() => {
    if (selectedType === 'array') {
      setValue('array_type', 'string');
      setSelectedArrayType('string');
    }
    if (selectedType === 'code') {
      setValue('code_language', 'javascript');
      setSelectedLanguage('javascript');
    }
  }, [selectedType, setValue]);

  const handleSpecificValueChange = (index, value) => {
    const newValues = [...specificValues];
    newValues[index] = value;
    setSpecificValues(newValues);

    if (newValues.some(v => v.trim() !== '')) {
      clearErrors('specific_values');
    }
    setValue('specific_values', newValues.filter(v => v.trim() !== ''));
  };

  const handleSpecificValueDelete = (index) => {
    const newValues = specificValues.filter((_, i) => i !== index);
    setSpecificValues(newValues.length ? newValues : ['']);
    setValue('specific_values', newValues.filter(v => v !== ''));
  };

  const handleClose = () => {
    onClose();
    clearFields();
    reset()
  }

  const formatRichTextValue = (text) => {
    if (Array.isArray(text)) {
      return text;
    }
    try {

      return Array.isArray(text) ? text : [
        {
          type: 'paragraph',
          children: [{ text: text || '' }]
        }
      ];
    } catch {
      return [
        {
          type: 'paragraph',
          children: [{ text: text || '' }]
        }
      ];
    }
  };


  return (
    <Modal open={isOpen} onClose={handleClose}>
      <ModalBox sx={{
        width: { xs: '95%', sm: '600px', md: '700px' },
        maxHeight: { xs: '95vh', sm: '90vh' },
      }}>
        <Typography variant="h6">{fieldData ? "Edit Field" : "Add Field"}</Typography>

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab label="Type" />
          <Tab
            label="Configuration"
            disabled={!selectedType}
          />
        </Tabs>

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Field Type Selection with Icons */}
          {activeTab === 0 && (
            <>
              <FormControl component="fieldset" margin="normal" variant="standard" fullWidth>
                <FormLabel component="legend">Select Field Type</FormLabel>
                <Controller
                  name="type"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: "Field type is required.",
                  }}
                  render={({ field }) => (
                    <>
                      <RadioGroup
                        {...field}
                        row
                        value={field.value}
                        onChange={(event) => {
                          field.onChange(event.target.value);
                          handleTabChange(null, 1)
                        }}
                        sx={{
                          justifyContent: 'space-between'
                        }}
                      >
                        {fieldTypes.map(({ value, label, icon, description }) => (
                          <FormControlLabel
                            key={value}
                            value={value}
                            sx={{
                              width: isMobile ? '49%' : '30%',
                              marginLeft: 0,
                              // border:'1px solid #E6E6E6', 
                              borderWidth: 1,
                              borderColor: field.value === value ? "primary.main" : "#E6E6E6",
                              borderStyle: "solid",
                              marginRight: 0,
                              marginBottom: 2,
                              alignItems: 'flex-start',
                              p: 2,
                              borderRadius: 2,
                              opacity: fieldEdit ? 0.6 : 1, // Reduce opacity in edit mode
                              pointerEvents: fieldEdit ? "none" : "auto", // Prevent interactions

                            }}
                            control={
                              <Radio
                                icon={<IconButton disabled={fieldEdit}>{icon}</IconButton>}
                                checkedIcon={
                                  <IconButton color="primary" disabled={fieldEdit}>
                                    {icon}
                                  </IconButton>
                                }
                                sx={{ display: "none", marginBottom: 4 }} // Hide default radio appearance
                              />

                            }
                            label={
                              <Box sx={{
                                display: 'flex',
                                flexDirection: 'column'
                              }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <IconButton
                                    color={
                                      field.value === value ? "primary" : "default"
                                    }
                                    onClick={() => {
                                      if (!fieldEdit) {
                                        field.onChange(value); // Set the value when icon is clicked
                                        handleTabChange(null, 1);
                                      }
                                    }}
                                    disabled={fieldEdit} // Disable click event
                                  >
                                    {icon}
                                  </IconButton>
                                  <Typography variant="caption">{label}</Typography>
                                </Box>
                                <Divider color={field.value === value ? "#003638" : "#ffffff"}></Divider>
                                <Box>
                                  <Typography variant="p" sx={{ fontSize: '14px', color: field.value === value ? "#003638" : "#6B6B6B" }}> {description}</Typography>
                                </Box>

                              </Box>
                            }
                            labelPlacement="bottom"
                          />
                        ))}
                      </RadioGroup>
                      {errors.type && (
                        <Typography color="error" variant="caption">
                          {errors.type.message}
                        </Typography>
                      )}
                    </>
                  )}
                />
              </FormControl>

              {/* <AddButton fullWidth onClick={handleNext} label="Next" /> */}
            </>
          )}

          {activeTab === 1 && (
            <>
              <FormLabel component="legend" sx={{ pb: 2 }}>Configuration</FormLabel>
              <FormControl variant="standard" fullWidth>
                <InputLabel shrink htmlFor="name">
                  Field Name
                </InputLabel>
                <OutlinedInput
                  {...register("name", {
                    required: "Field name is required.",
                    minLength: {
                      value: 2,
                      message: "Field name must be at least 3 characters.",
                    },
                  })}
                  margin="normal"
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  onChange={(e) => transformFieldId(e.target.value)}
                  disabled={fieldEdit}
                />
              </FormControl>

              <FormControl variant="standard" fullWidth>
                <InputLabel shrink htmlFor="name">
                  Field Id
                </InputLabel>
                <OutlinedInput
                  {...register("id", {
                    required: "Field ID is required.",
                    pattern: {
                      value: /^[a-zA-Z0-9-_]+$/,
                      message: "Field ID must be alphanumeric and can include - or _.",
                    },
                  })}
                  margin="normal"
                  error={!!errors.id}
                  helperText={errors.id?.message}
                  disabled={fieldEdit}
                />
              </FormControl>
              {selectedType === 'string' && (
                <FormControl component="fieldset" variant="standard" fullWidth>
                  <Controller
                    name="variant"
                    control={control}
                    defaultValue={fieldData?.variant || ""}
                    rules={{
                      required: selectedType === 'string' ? "String type is required." : false,
                    }}
                    render={({ field }) => (
                      <>
                        <Typography
                          sx={{
                            fontSize: '0.9rem',
                            color: 'black',
                            fontWeight: 400,
                            mb: 1
                          }}
                        >
                          Select String Type
                        </Typography>
                        <RadioGroup
                          {...field}
                          row
                          value={field.value}
                          onChange={(e) => {
                            const value = e.target.value;
                            field.onChange(value);
                            // Reset configuration when changing variant
                            if (value !== 'shortText') {
                              setSelectedStringConfig('none');
                              setSpecificValues(['']);
                              setValue('default_value', '');
                              setValue('specific_values', undefined);
                            }
                          }}
                          sx={{
                            justifyContent: 'space-between'
                          }}
                        >
                          {stringConfigurations.map(({ value, label, icon, description }) => (

                            <FormControlLabel
                              key={value}
                              value={value}
                              sx={{
                                width: '32%',
                                marginLeft: 0,
                                borderWidth: 1,
                                borderColor: field.value === value ? "primary.main" : "#E6E6E6",
                                borderStyle: "solid",
                                marginRight: 0,
                                marginBottom: 1,
                                alignItems: 'flex-start',
                                p: 2,
                                borderRadius: 2
                              }}
                              control={
                                <Radio
                                  icon={<IconButton>{icon}</IconButton>}
                                  checkedIcon={
                                    <IconButton color="primary">{icon}</IconButton>
                                  }
                                  sx={{ display: "none" }}
                                />
                              }
                              label={
                                <Box sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <IconButton
                                      color={field.value === value ? "primary" : "default"}
                                      onClick={() => field.onChange(value)}
                                    >
                                      {icon}
                                    </IconButton>
                                    <Typography variant="caption">{label}</Typography>
                                  </Box>
                                  <Divider color={field.value === value ? "#003638" : "#ffffff"} />
                                  <Box>
                                    <Typography variant="p" sx={{ fontSize: '14px', color: field.value === value ? "#003638" : "#6B6B6B" }}>
                                      {description}
                                    </Typography>
                                  </Box>
                                </Box>
                              }
                              labelPlacement="bottom"
                            />
                          ))}
                        </RadioGroup>
                        {errors.variant && (
                          <Typography color="error" variant="caption">
                            {errors.variant.message}
                          </Typography>
                        )}
                      </>
                    )}
                  />
                  {watch('variant') === 'slugField' && (
                    <FormControl variant="standard" fullWidth sx={{ mb: 2 }}>
                      <InputLabel shrink className="text-sm">Select reference field</InputLabel>
                      <Select
                        value={selectedReferenceSlug}
                        onChange={(e) => {
                          setSelectedReferenceSlug(e.target.value);
                          setValue('referenceSlugEntry', e.target.value); // save into form
                        }}
                      >
                        {/* String Field and short text variant */}
                        {allFields
                          .filter(field =>
                            field.id !== watch('id') &&
                            field.type === 'string' &&
                            field.variant === 'shortText'
                          )
                          .map((field) => (
                            <MenuItem key={field.id} value={field.id}>
                              {field.name}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  )}
                  {selectedType === 'string' && watch('variant') === 'shortText' && (
                    <FormControl variant="standard" fullWidth sx={{ mb: 2 }}>
                      <RadioGroup row value={selectedStringConfig}
                        onChange={(e) => {
                          setSelectedStringConfig(e.target.value);
                          clearErrors(['default_value', 'specific_values']);
                        }}>
                        <FormControlLabel value="none" control={<Radio />} label="None" />
                        <FormControlLabel value="default_value" control={<Radio />} label="Default Value" />
                        <FormControlLabel value="specific_value" control={<Radio />} label="Specific Value" />
                      </RadioGroup>
                    </FormControl>
                  )}
                </FormControl>
              )}

              {selectedType === 'code' && (
                <FormControl variant="standard" fullWidth sx={{ mb: 2 }}>
                  <InputLabel shrink className="text-sm">Language</InputLabel>
                  <Select
                    value={selectedLanguage}
                    onChange={(e) => {
                      setSelectedLanguage(e.target.value);
                      setValue('code_language', e.target.value);
                    }}
                  >
                    <MenuItem value="javascript">JavaScript</MenuItem>
                    <MenuItem value="html">HTML</MenuItem>
                    <MenuItem value="css">CSS</MenuItem>
                    <MenuItem value="json">JSON</MenuItem>
                    <MenuItem value="python">Python</MenuItem>
                    <MenuItem value="java">Java</MenuItem>
                    <MenuItem value="xml">XML</MenuItem>
                  </Select>
                </FormControl>
              )}

              {selectedStringConfig === 'default_value' ? (
                <FormControl variant="standard" fullWidth>
                  <InputLabel shrink htmlFor="defaultValue">
                    Default value
                  </InputLabel>
                  <OutlinedInput
                    {...register("default_value", {
                      required: selectedStringConfig === 'default_value' ? 'Default value is required' : false
                    })}
                    margin="normal"
                    error={!!errors.default_value}
                  />
                  {errors.default_value && (
                    <Typography color="error" variant="caption">
                      {errors.default_value.message}
                    </Typography>
                  )}
                </FormControl>
              ) : selectedStringConfig === 'specific_value' && watch('variant') === 'shortText' ? (
                <Box>
                  <Typography
                    sx={{
                      fontSize: '0.9rem',
                      color: 'black',
                      fontWeight: 400,
                      mb: 1
                    }}
                  >
                    Specific Values
                  </Typography>
                  <Box
                    sx={{
                      '&:before': { display: 'none' },
                      boxShadow: 'none',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      p: 1,
                      mb: 3
                    }}>
                    {specificValues.map((value, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <OutlinedInput
                          value={value}
                          onChange={(e) => {
                            handleSpecificValueChange(index, e.target.value);

                          }}
                          size="small"
                          fullWidth
                          error={!!errors.specific_values}
                          placeholder={`Value ${index + 1}`}
                          InputProps={{
                            sx: {
                              '& input::placeholder': {
                                fontSize: '0.975rem',
                              }
                            }
                          }}
                        />
                        <IconButton
                          color="error"
                          onClick={() => handleSpecificValueDelete(index)}
                          size="small"
                        >
                          <CloseIcon />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      startIcon={<AddIcon />}
                      variant="outlined"
                      size="small"
                      onClick={() => setSpecificValues([...specificValues, ''])}
                      sx={{ mt: 1 }}
                    >
                      Add Value
                    </Button>
                    {errors.specific_values && (
                      <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                        {errors.specific_values.message}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ) : (<></>)}

              < Box sx={{
                paddingBottom: 2
              }}>
                {(selectedType === 'number') && (
                  <FormControl variant="standard" fullWidth sx={{ mt: 2 }}>
                    <InputLabel shrink htmlFor="defaultValue">
                      {defaultValueConfig[selectedType].label}
                    </InputLabel>
                    <OutlinedInput
                      {...register("default_value", {
                        validate: {
                          isNumber: (value) =>
                            !value || validateNumber(value) || "Please enter a valid number"
                        }
                      })}
                      margin="normal"
                      type="text"
                      error={!!errors.default_value}
                      onKeyPress={(e) => {
                        // Allow only numbers, decimal point, and control keys
                        if (!/[\d.]|\./g.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete') {
                          e.preventDefault();
                        }
                        // Prevent multiple decimal points
                        const currentValue = e.target.value;
                        if (e.key === '.' && currentValue.includes('.')) {
                          e.preventDefault();
                        }
                      }}
                    />
                    {errors.default_value && (
                      <Typography color="error" variant="caption">
                        {errors.default_value.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
                {(selectedType === 'richText') && (
                  <FormControl variant="standard" fullWidth sx={{ mt: 2 }}>
                    <Typography
                      sx={{
                        fontSize: '0.9rem',
                        color: 'black',
                        fontWeight: 400,
                      }}
                    >
                      {defaultValueConfig[selectedType].label}
                    </Typography>
                    <RichText
                      value={formatRichTextValue(watch('default_value'))}
                      onChange={(value) => {
                        setValue('default_value', value);
                      }}
                    />
                  </FormControl>
                )}

                {(selectedType === 'string' || selectedType === 'richText') &&
                  selectedStringConfig !== 'specific_value' &&
                  (watch('enable_ai') || (fieldEdit && AIPrompt && (watch('enable_ai') === undefined || watch('enable_ai') === null))) && (
                    <FormControl variant="standard" sx={{ mt: 1 }} fullWidth>
                      <Typography
                        sx={{
                          fontSize: '0.9rem',
                          color: 'black',
                          fontWeight: 400,
                          mb: 1
                        }}
                      >
                        Enter your predefined AI prompt
                      </Typography>
                      <StyledTextArea
                        value={AIPrompt}
                        onChange={(e) => handleAIPromptChange(e.target.value)}
                        sx={{
                          '& .MuiInputBase-input.Mui-disabled': {
                            WebkitTextFillColor: 'black',
                            color: 'black',
                          },
                        }}
                      />
                      {errors.enable_ai && (
                        <Typography color="error" variant="caption">
                          {errors.enable_ai.message}
                        </Typography>
                      )}
                    </FormControl>
                  )}

                {(selectedType === 'dateTime') && (
                  <FormControl variant="standard" fullWidth sx={{ mt: 2 }}>
                    <Typography
                      sx={{
                        fontSize: '0.9rem',
                        color: 'black',
                        fontWeight: 400,
                      }}
                    >
                      {defaultValueConfig[selectedType].label}
                    </Typography>
                    <DateTimeEntry
                      value={watch('default_value') || {
                        date: '',
                        time: '',
                        timeZone: ''
                      }}
                      onChange={(value) => {
                        setValue('default_value', value);
                      }}
                      formatEdit={true}
                    />
                  </FormControl>
                )}

                {(selectedType === 'array') && (
                  <FormControl variant="standard" fullWidth sx={{ mb: 2 }}>
                    <RadioGroup value={selectedArrayType} onChange={(e) => {
                      const value = e.target.value;
                      setSelectedArrayType(value);
                      setValue('array_type', value);
                    }}>
                      <FormControlLabel value="string" control={<Radio />} label="String" />
                      <FormControlLabel value="number" control={<Radio />} label="Number" />
                    </RadioGroup>
                  </FormControl>
                )}

                {(selectedType === 'object') && (
                  <FormControl variant="standard" fullWidth sx={{ mb: 2 }}>
                    <RadioGroup
                      value={selectedDisplayOption}
                      onChange={(e) => {
                        setSelectedDisplayOption(e.target.value);
                        if (e.target.value === 'default') {
                          setValue('display', undefined);
                          setValue('localizations', true);
                        }
                      }}
                    >
                      <FormControlLabel value="default" control={<Radio />} label="Use default display" />
                      <FormControlLabel value="customApp" control={<Radio />} label="Use custom display" />
                    </RadioGroup>
                  </FormControl>
                )}

                {(selectedDisplayOption === 'customApp') && (
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel
                      sx={{
                        fontSize: "1rem",
                        color: "gray",
                      }}
                    >
                      Select Custom App
                    </InputLabel>
                    <Select
                      value={selectedApp || ''}
                      onChange={(e) => {
                        const selectedAppData = installedApps.find(app => app._id === e.target.value);
                        setSelectedApp(e.target.value);
                        setValue('display', {
                          appName: selectedAppData.appName,
                          url: selectedAppData.url
                        });
                        setValue('localizations', false);
                      }}
                      label="Select custom app"
                      sx={{
                        fontSize: "1.1rem",
                        color: "#333",
                      }}
                    >
                      {installedApps.length > 0 ? (
                        installedApps.map((installedApp) => (
                          <MenuItem
                            key={installedApp._id}
                            value={installedApp._id}
                            sx={{
                              fontSize: "1.1rem",
                            }}
                          >
                            <Stack spacing={1}>
                              <Typography sx={{ fontSize: '1rem' }}>
                                {installedApp.appName}
                              </Typography>
                              <Typography sx={{ fontSize: '0.9rem', color: 'gray' }}>
                                {installedApp.url}
                              </Typography>
                            </Stack>
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled>
                          <Typography
                            sx={{
                              fontSize: "1.1rem",
                              color: "gray",
                            }}
                          >
                            No custom apps
                          </Typography>
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                )}

                {selectedType && fieldSettings[selectedType]?.map(setting => (
                  <FormControlLabel
                    key={setting}
                    control={
                      <Controller
                        name={setting}
                        control={control}
                        defaultValue={!!fieldData?.[setting]}
                        render={({ field }) => (
                          <Checkbox
                            checked={field?.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                            disabled={
                              (setting === 'enable_ai' || setting === 'localizations') &&
                              selectedStringConfig === 'specific_value'
                            }
                          />
                        )}
                      />
                    }

                    label={setting.charAt(0).toUpperCase() + setting.slice(1).replace('_', ' ')}
                  />
                ))}

                {(selectedType === 'string' || selectedType === 'number') && (
                  <FormControlLabel
                    control={
                      <Controller
                        name="findable"
                        control={control}
                        defaultValue={!!fieldData?.findable}
                        render={({ field }) => (
                          <Checkbox
                            {...field}
                            checked={field.value}
                          />
                        )}
                      />
                    }
                    label="Find by this field"
                  />
                )}

              </Box>
              <AddButton fullWidth type="submit" label="Add Field" />
            </>
          )}
        </form>
      </ModalBox>
    </Modal >
  );
};

export default FieldModal;
